import {
  Home,
  Blocks,
  ShoppingCart,
  MessageSquare,
  Brain,
  Package,
  Boxes,
  Archive,
  GitBranch,
  ClipboardList,
  BarChart3,
  Receipt,
  ShipWheel,
  Gauge,
  Users,
  UsersRound,
  Building,
  Settings,
  BookA,
  type LucideProps,
} from "lucide-react";

import type { FeatureFlags } from "@/app/api/types/featureFlags";

export type SidebarItem = {
  id: string;
  titleKey: string;
  url: string;
  icon: React.ComponentType<LucideProps>;
  notification?: string;
  featureFlagKey: keyof FeatureFlags;
};

export const allSidebarItems: SidebarItem[] = [
  {
    id: "home",
    titleKey: "navigation.home",
    url: "/home",
    icon: Home,
    featureFlagKey: "sidebarShowHome",
  },
  {
    id: "monitoring",
    titleKey: "navigation.monitoring",
    url: "/monitoring",
    icon: Gauge,
    featureFlagKey: "sidebarShowMonitoring",
  },
  {
    id: "purchases",
    titleKey: "navigation.purchases",
    url: "/purchases",
    icon: Blocks,
    notification: "order",
    featureFlagKey: "sidebarShowPurchases",
  },
  {
    id: "recap",
    titleKey: "navigation.recap",
    url: "/recap",
    icon: ShoppingCart,
    featureFlagKey: "sidebarShowRecap",
  },
  {
    id: "chat",
    titleKey: "navigation.chat",
    url: "/chat",
    icon: MessageSquare,
    featureFlagKey: "sidebarShowChat",
  },
  {
    id: "ai",
    titleKey: "common.aiManagement.title",
    url: "/ai",
    icon: Brain,
    featureFlagKey: "sidebarShowAI",
  },
  {
    id: "products",
    titleKey: "navigation.products",
    url: "/products",
    icon: Package,
    featureFlagKey: "sidebarShowProducts",
  },
  {
    id: "stocks",
    titleKey: "navigation.stocks",
    url: "/stocks",
    icon: Boxes,
    featureFlagKey: "sidebarShowStocks",
  },
  {
    id: "lots",
    titleKey: "navigation.lots",
    url: "/lots",
    icon: Archive,
    featureFlagKey: "sidebarShowLots",
  },
  {
    id: "flows",
    titleKey: "navigation.flows",
    url: "/flows",
    icon: GitBranch,
    featureFlagKey: "sidebarShowFlows",
  },
  {
    id: "pricing",
    titleKey: "navigation.pricing",
    url: "/pricing",
    icon: ClipboardList,
    featureFlagKey: "sidebarShowPricing",
  },
  {
    id: "reports",
    titleKey: "navigation.reports",
    url: "/reports",
    icon: BarChart3,
    featureFlagKey: "sidebarShowReports",
  },
  {
    id: "operations",
    titleKey: "navigation.operations",
    url: "/operations",
    icon: Receipt,
    featureFlagKey: "sidebarShowOperations",
  },
  {
    id: "steering",
    titleKey: "navigation.businessIntelligence",
    url: "/steering",
    icon: ShipWheel,
    featureFlagKey: "sidebarShowBusinessIntelligence",
  },
  {
    id: "partners",
    titleKey: "navigation.partners",
    url: "/partners",
    icon: Users,
    featureFlagKey: "sidebarShowPartners",
  },
  {
    id: "companyManagement",
    titleKey: "navigation.companyManagement",
    url: "/company",
    icon: Building,
    featureFlagKey: "sidebarShowCompanyManagement",
  },
  {
    // Group-admin surface ("Your groups" → per-group user management). Relabels
    // to "Agencies" in transport/agency mode. Visibility is additionally gated
    // in the sidebar to users who actually administer a group (or company
    // owners) — the flag alone doesn't expose it to plain members.
    id: "groups",
    titleKey: "navigation.groups",
    url: "/groups",
    icon: UsersRound,
    featureFlagKey: "sidebarShowGroups",
  },
  {
    id: "dictionary",
    titleKey: "navigation.dictionary",
    url: "/dictionary",
    icon: BookA,
    featureFlagKey: "sidebarShowDictionary",
  },
  {
    id: "parameters",
    titleKey: "navigation.parameters",
    url: "/parameters",
    icon: Settings,
    featureFlagKey: "sidebarShowParameters",
  },
];

// Lookup by item id. Module-scoped so consumers don't re-allocate it per render.
export const sidebarItemById = new Map(
  allSidebarItems.map((item) => [item.id, item]),
);

// Sidebar items that are hidden until their flag is explicitly enabled (opt-in),
// as opposed to the legacy opt-out items that show unless explicitly disabled.
export const OPT_IN_SIDEBAR_ITEMS = new Set<string>([
  "home",
  "monitoring",
  "dictionary",
  "groups",
]);

// Single source of truth for a sidebar item's visibility given the effective
// feature flags. Legacy items are opt-OUT (shown unless explicitly false, for
// backwards compatibility); opt-IN items stay hidden until explicitly enabled.
// Reused by the preferences default-page selector so the landing-page choices
// stay in lockstep with what the user can actually see in the sidebar.
export function isSidebarItemEnabled(
  item: SidebarItem,
  featureFlags: FeatureFlags | undefined,
): boolean {
  if (!featureFlags) return false;
  const flagValue = featureFlags[item.featureFlagKey];
  if (OPT_IN_SIDEBAR_ITEMS.has(item.id)) return flagValue === true;
  return flagValue !== false;
}

// Title-key overrides applied when the company is in transport/logistics mode.
// Kept here (not just in the sidebar) so any consumer resolving a page label —
// e.g. the preferences default-page selector — matches the sidebar exactly.
export const TRANSPORT_TITLE_KEYS: Record<string, string> = {
  partners: "navigation.partners_transport",
  products: "navigation.products_transport",
  recap: "navigation.recap_transport",
  purchases: "navigation.purchases_transport",
  groups: "navigation.groups_transport",
};

// Resolve the effective title key for a page id, honouring transport mode.
export function sidebarItemTitleKey(
  id: string,
  featureFlags: FeatureFlags | undefined,
): string | undefined {
  if (featureFlags?.transportMode === true && TRANSPORT_TITLE_KEYS[id]) {
    return TRANSPORT_TITLE_KEYS[id];
  }
  return sidebarItemById.get(id)?.titleKey;
}
